import type {
    GetServerSideProps,
    GetServerSidePropsContext,
    NextComponentType,
    NextPageContext,
    PreviewData
} from 'next';
import type { ParsedUrlQuery } from 'querystring';
import type { NextProps } from './optimizely.types';

import cookies from 'nookies';

import {
    addOptimizelyProps,
    WithOptimizelyPageProps,
    DistributeWithOptimizelyPageProps
} from './hooks/useOptimizelyProviderProps';
import { getOptimizelyUserInfo } from './optimizely';
import { getOptimizelyOverrides } from './optimizelyOverrides';
import { getDataFile } from './optimizelyDataFile';
import { getAnonId } from './getAnonIdFromContext';

type GetInitialPageProps<P, IP = P> = Required<
    NextComponentType<NextPageContext, IP, P>
>['getInitialProps'];
type PageCallback<P> = GetInitialPageProps<P>;

/**
 * To support legacy getInitialProps we MUST support isomorphic functionality,
 * this can be removed once we are no longer using getInitialProps in our pages.
 * To support Optimizely we must pull user specific data from the context (which
 * is only available in data-fetching methods) of the request and pass into the
 * optimizely provider in _app.
 */
const makeProps = async (
    context: NextPageContext | GetServerSidePropsContext
) => {
    const allCookies = cookies.get(context);
    const datafile = await getDataFile();
    const anonId = getAnonId(context) as string;

    const userAgent =
        typeof window === 'undefined'
            ? context.req?.headers['user-agent']
            : window.navigator.userAgent;

    const optimizelyOverrides = getOptimizelyOverrides(
        allCookies,
        context.query ?? {}
    );

    const user = getOptimizelyUserInfo(anonId, userAgent);
    return { user, datafile, optimizelyOverrides };
};

const getServerSideProps =
    <
        P extends NextProps = NextProps,
        Q extends ParsedUrlQuery = ParsedUrlQuery,
        D extends PreviewData = PreviewData
    >(
        callback: GetServerSideProps<P, Q, D>
    ): GetServerSideProps<DistributeWithOptimizelyPageProps<P>, Q, D> =>
    async (context) => {
        const initialProps = (callback && (await callback(context))) || {};

        if ('redirect' in initialProps) {
            return initialProps;
        }

        if ('notFound' in initialProps) {
            return initialProps;
        }

        if ('props' in initialProps) {
            const optimizelyProps = await makeProps(context);

            // props can be a promise https://github.com/vercel/next.js/pull/28607
            const isPromise =
                typeof initialProps.props === 'object' &&
                typeof initialProps.props.then === 'function';

            if (isPromise) {
                return {
                    props: (initialProps as { props: Promise<P> }).props.then(
                        (props) => addOptimizelyProps(optimizelyProps, props)
                    )
                };
            } else {
                return {
                    props: addOptimizelyProps(
                        optimizelyProps,
                        (initialProps as { props: P }).props
                    )
                };
            }
        }
        return initialProps;
    };

export const getInitialProps =
    <P = {}>( // eslint-disable-line @typescript-eslint/ban-types
        callback: PageCallback<P>
    ): GetInitialPageProps<P & WithOptimizelyPageProps> =>
    async (context) => {
        const props = (callback && (await callback(context))) || ({} as P);
        const optimizelyProps = await makeProps(context);
        return addOptimizelyProps(optimizelyProps, props);
    };

/**
 * An "empty" getServerSideProps function, that just enables Optimizely SSR
 */
export const _getServerSideProps = getServerSideProps(() =>
    Promise.resolve({ props: {} })
);

export default {
    getServerSideProps,
    getInitialProps
};
